import { useApolloClient } from '@apollo/client';
import { useCallback, useMemo, useState } from 'react';

import {
  BuyerJobCutsheetByIdDocument,
  type BuyerJobCutsheetByIdQuery,
  Cutsheet,
  useProcessorSchedulingCutsheetsPartialUpdateMutation,
} from 'lib/graphql';

import {
  type DrawerCutsheetInfo,
  type DrawerHead,
  type DrawerJob,
  asInspectionLevel,
  asSpecies,
} from '../split-slot/types';

import { useDownloadCutsheetPdf } from './use-download-cutsheet-pdf';
import { useWizardData } from './use-wizard-data';

import type {
  BuyerOrderItem,
  BuyerSlotInfo,
} from '../../buyer/use-buyer-orders';

type DrawerContext = {
  item: BuyerOrderItem;
  slot: BuyerSlotInfo;
} | null;

type QuestionnaireDrawerContext = DrawerContext;

interface UseOrderDrawerActionsOptions {
  userId: string;
  refetchOrders: () => Promise<void>;
}

export function useOrderDrawerActions({
  userId,
  refetchOrders,
}: UseOrderDrawerActionsOptions) {
  const apolloClient = useApolloClient();

  // --- Shared cutsheet drawer state ---
  const [drawerCtx, setDrawerCtx] = useState<DrawerContext>(null);

  // --- Questionnaire drawer/wizard state ---
  const [questionnaireResponseDrawerCtx, setQuestionnaireResponseDrawerCtx] =
    useState<QuestionnaireDrawerContext>(null);
  const [questionnaireEditWizardCtx, setQuestionnaireEditWizardCtx] =
    useState<QuestionnaireDrawerContext>(null);

  // --- Shared wizard state ---
  const [wizardCtx, setWizardCtx] = useState<DrawerContext>(null);
  const {
    wizardDataReady,
    setWizardDataReady,
    wizardCapabilities,
    wizardSettings,
    templateCutsheetRef,
    fetchWizardConfig,
    fetchWizardDataAndSetReady: fetchWizardData,
  } = useWizardData();

  // --- Mutations ---
  const { generatingPdf, downloadPdf } = useDownloadCutsheetPdf();

  const [assignCutsheetToSlot] =
    useProcessorSchedulingCutsheetsPartialUpdateMutation();

  // --- Callbacks ---
  const handleFillCutsheet = useCallback(
    (item: BuyerOrderItem, slot: BuyerSlotInfo) => {
      if (slot.questionnaireId) {
        setQuestionnaireResponseDrawerCtx({ item, slot });
        return;
      }
      setDrawerCtx({ item, slot });
    },
    [],
  );

  const handleEditCutsheet = useCallback(
    async (item: BuyerOrderItem, slot: BuyerSlotInfo) => {
      if (slot.questionnaireId) {
        if (slot.questionnaireResponseId) {
          setQuestionnaireEditWizardCtx({ item, slot });
        } else {
          setQuestionnaireResponseDrawerCtx({ item, slot });
        }
        return;
      }

      setWizardDataReady(false);
      setWizardCtx({ item, slot });

      const [, cutsheetResult] = await Promise.all([
        fetchWizardConfig(item.jobId, userId),
        apolloClient.query<BuyerJobCutsheetByIdQuery>({
          query: BuyerJobCutsheetByIdDocument,
          variables: { jobId: item.jobId },
          fetchPolicy: 'no-cache',
        }),
      ]);

      const heads =
        cutsheetResult.data?.processorSchedulingById?.animalHeads ?? [];
      let resolved = false;
      for (const head of heads) {
        const info = head?.cutsheetInformation?.find(
          (ci) => ci?._id === slot.cutsheetInfoId,
        );
        if (info?.cutsheet) {
          templateCutsheetRef.current = info.cutsheet as Cutsheet;
          resolved = true;
          break;
        }
      }

      if (!resolved) {
        templateCutsheetRef.current = slot.raw.cutsheet as Cutsheet | undefined;
      }

      setWizardDataReady(true);
    },
    [apolloClient, fetchWizardConfig, userId],
  );

  const handleDownloadPdf = useCallback(
    async (item: BuyerOrderItem, slot: BuyerSlotInfo) => {
      if (!slot.cutsheetInfoId) return;
      await downloadPdf(item.jobId, slot.cutsheetInfoId);
    },
    [downloadPdf],
  );

  const handleWizardCutsheetCreated = useCallback(
    async (cutsheet: Cutsheet) => {
      if (!wizardCtx) return;
      await assignCutsheetToSlot({
        variables: {
          processorSchedulingId: wizardCtx.item.jobId,
          record: [
            { _id: wizardCtx.slot.cutsheetInfoId, cutsheet: cutsheet._id },
          ],
        },
      });
      setWizardCtx(null);
      templateCutsheetRef.current = undefined;
      await refetchOrders();
    },
    [wizardCtx, assignCutsheetToSlot, refetchOrders],
  );

  const handleDrawerCreateNew = useCallback(() => {
    if (!drawerCtx) return;
    const { item, slot } = drawerCtx;
    templateCutsheetRef.current = undefined;
    setDrawerCtx(null);
    setWizardCtx({ item, slot });
    fetchWizardData(item.jobId, userId);
  }, [drawerCtx, fetchWizardData, userId]);

  // Build drawer-compatible objects using narrower types.
  const drawerJob: DrawerJob | null = useMemo(() => {
    if (!drawerCtx) return null;
    return drawerCtx.item.job;
  }, [drawerCtx]);

  const drawerHead: DrawerHead | null = useMemo(() => {
    if (!drawerCtx) return null;
    return drawerCtx.item.head;
  }, [drawerCtx]);

  const drawerCutsheetInfo: DrawerCutsheetInfo | null = useMemo(() => {
    if (!drawerCtx) return null;
    return drawerCtx.slot.raw;
  }, [drawerCtx]);

  // Wizard context
  const wizardAnimalSpecies = asSpecies(wizardCtx?.item.animalSpecies);
  const wizardInspectionLevel = asInspectionLevel(
    wizardCtx?.item.head.inspectionLevel,
  );

  return {
    // Drawer state
    drawerCtx,
    setDrawerCtx,
    drawerJob,
    drawerHead,
    drawerCutsheetInfo,

    // Questionnaire state
    questionnaireResponseDrawerCtx,
    setQuestionnaireResponseDrawerCtx,
    questionnaireEditWizardCtx,
    setQuestionnaireEditWizardCtx,

    // Wizard state
    wizardCtx,
    setWizardCtx,
    wizardDataReady,
    wizardCapabilities,
    wizardSettings,
    templateCutsheetRef,
    wizardAnimalSpecies,
    wizardInspectionLevel,

    // PDF state
    generatingPdf,

    // Callbacks
    handleFillCutsheet,
    handleEditCutsheet,
    handleDownloadPdf,
    handleWizardCutsheetCreated,
    handleDrawerCreateNew,
  };
}
